import React from 'react';
import Icon from '../../../components/AppIcon';

const ExerciseStats = ({ exercises }) => {
  const totalExercises = exercises?.length;
  const activeExercises = exercises?.filter(ex => ex?.isActive)?.length;
  const inactiveExercises = totalExercises - activeExercises;

  const categoryStats = exercises?.reduce((acc, exercise) => {
    const category = exercise?.category;
    acc[category] = (acc?.[category] || 0) + 1;
    return acc;
  }, {});

  const difficultyStats = exercises?.reduce((acc, exercise) => {
    exercise?.difficultyLevels?.forEach(level => {
      const difficulty = level?.phase;
      acc[difficulty] = (acc?.[difficulty] || 0) + 1;
    });
    return acc;
  }, {});

  const getCategoryIcon = (category) => {
    const categoryIcons = {
      'strength': 'Dumbbell',
      'cardio': 'Heart',
      'flexibility': 'Zap',
      'balance': 'Target',
      'rehabilitation': 'Shield',
      'mobility': 'RotateCcw'
    };
    return categoryIcons?.[category?.toLowerCase()] || 'Activity';
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'beginner': return 'text-success bg-success/10';
      case 'intermediate': return 'text-warning bg-warning/10';
      case 'advanced': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
      {/* Total Exercises */}
      <div className="bg-surface rounded-xl border border-border p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">Total Exercises</p>
            <p className="text-3xl font-bold text-foreground">{totalExercises}</p>
          </div>
          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Database" size={24} className="text-primary" />
          </div>
        </div>
      </div>
      {/* Active Exercises */}
      <div className="bg-surface rounded-xl border border-border p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">Active</p>
            <p className="text-3xl font-bold text-success">{activeExercises}</p>
            <p className="text-xs text-muted-foreground">
              {totalExercises > 0 ? Math.round((activeExercises / totalExercises) * 100) : 0}% of total
            </p>
          </div>
          <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
            <Icon name="CheckCircle" size={24} className="text-success" />
          </div>
        </div>
      </div>
      {/* Inactive Exercises */}
      <div className="bg-surface rounded-xl border border-border p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">Inactive</p>
            <p className="text-3xl font-bold text-muted-foreground">{inactiveExercises}</p>
            <p className="text-xs text-muted-foreground">
              {totalExercises > 0 ? Math.round((inactiveExercises / totalExercises) * 100) : 0}% of total
            </p>
          </div>
          <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
            <Icon name="EyeOff" size={24} className="text-muted-foreground" />
          </div>
        </div>
      </div>
      {/* Categories */}
      <div className="bg-surface rounded-xl border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm font-medium text-muted-foreground">Categories</p>
            <p className="text-3xl font-bold text-foreground">{Object.keys(categoryStats)?.length}</p>
          </div>
          <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center">
            <Icon name="Grid3X3" size={24} className="text-secondary" />
          </div>
        </div>
        
        <div className="space-y-2">
          {Object.entries(categoryStats)?.sort(([,a], [,b]) => b - a)?.slice(0, 3)?.map(([category, count]) => (
              <div key={category} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon name={getCategoryIcon(category)} size={16} className="text-muted-foreground" />
                  <span className="text-sm text-foreground capitalize">{category}</span>
                </div>
                <span className="text-sm font-medium text-muted-foreground">{count}</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default ExerciseStats;